import React, { useState, useEffect, useCallback } from 'react';
import { api } from './shared/utils/api';
import { formatDate } from './dateUtils';
import './LawDrillsReview.css';

export default function LawDrillsReview() {
  const [loading, setLoading]     = useState(true);
  const [batch, setBatch]         = useState(null);
  const [topics, setTopics]       = useState([]);   // L2-level results
  const [allL2s, setAllL2s]       = useState([]);   // override options
  const [overrides, setOverrides] = useState({});   // { result_id: sqetrack_general_topic | null }
  const [saving, setSaving]       = useState(false);
  const [feedback, setFeedback]   = useState(null);
  const [filter, setFilter]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const data = await api('/api/import/lawdrills/last');
      setBatch(data.batch);
      setTopics(data.topics || []);
      setAllL2s(data.all_l2s || []);
    } catch {
      setFeedback({ type: 'error', msg: 'Could not load import data. Check your connection and try again.' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const matched   = topics.filter(t => t.sqetrack_general_topic);
  const unmatched = topics.filter(t => !t.sqetrack_general_topic);

  const filteredMatched = filter.trim()
    ? matched.filter(t =>
        (t.sqetrack_general_topic || t.source_label || '')
          .toLowerCase().includes(filter.toLowerCase())
      )
    : matched;

  const pendingCount = Object.keys(overrides).length;

  async function handleSave() {
    if (!pendingCount) return;
    setSaving(true);
    setFeedback(null);
    try {
      const payload = Object.entries(overrides).map(([result_id, sqetrack_general_topic]) => ({
        result_id: parseInt(result_id, 10),
        sqetrack_general_topic: sqetrack_general_topic || null,
      }));
      const data = await api('/api/import/lawdrills/override', {
        method: 'POST',
        body: JSON.stringify({ overrides: payload }),
      });
      if (data.error) throw new Error(data.error || 'Save failed');
      setFeedback({ type: 'success', msg: `${data.applied} mapping${data.applied !== 1 ? 's' : ''} saved. Future imports will use these matches.` });
      setOverrides({});
      await load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e.message });
    }
    setSaving(false);
  }

  if (loading) return (
    <div className="ldr-wrap">
      <p className="ldr-meta" style={{ padding: '40px 0', textAlign: 'center' }}>Loading</p>
    </div>
  );

  if (!batch) return (
    <div className="ldr-wrap">
      <div className="ldr-empty">
        <strong>No Law Drills import found</strong>
        Use the SQETrack Chrome extension on the Law Drills performance page to import your results.
      </div>
    </div>
  );

  const importedAt = formatDate(batch.imported_at, 'full');

  return (
    <div className="ldr-wrap">

      {/* ── Import summary header ── */}
      <div className="ldr-summary-header">
        <div>
          <div className="ldr-title">Law Drills import complete</div>
          <div className="ldr-meta">
            Imported {importedAt} · {batch.paper} · {batch.topic_count} topics parsed
          </div>
        </div>
        <div className="ldr-pills">
          <span className="ldr-pill ldr-pill-paper">{batch.paper}</span>
          <span className="ldr-pill ldr-pill-ok">{matched.length} imported</span>
          {unmatched.length > 0 && (
            <span className="ldr-pill ldr-pill-unmatched">{unmatched.length} need review</span>
          )}
        </div>
      </div>

      {/* ── Matched L2 topics ── */}
      {matched.length > 0 && (
        <div className="ldr-group">
          <div className="ldr-matched-header">
            <div className="ldr-group-heading">
              Topics updated
              <span className="ldr-count-badge">{matched.length}</span>
            </div>
            <input
              className="ldr-filter-input"
              type="text"
              placeholder="Filter topics"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
          {filteredMatched.length > 0 ? (
            <table className="ldr-table ldr-summary-table">
              <thead>
                <tr>
                  <th>SQETrack topic area</th>
                  <th>Score</th>
                  <th>Questions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMatched.map(t => (
                  <tr key={t.id}>
                    <td className="ldr-source">
                      {t.sqetrack_general_topic || t.source_label}
                      {t.l3_source_labels && t.l3_source_labels.length > 1 && (
                        <span className="ldr-l3-hint">
                          {t.l3_source_labels.length} subtopics averaged
                        </span>
                      )}
                    </td>
                    <td className="ldr-score">{t.score_pct != null ? `${Math.round(t.score_pct)}%` : '—'}</td>
                    <td className="ldr-score">{t.attempted != null ? `${t.attempted}q` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="ldr-no-results">No topics match "{filter}"</p>
          )}
        </div>
      )}

      {/* ── Unmatched notice ── */}
      {unmatched.length > 0 && (
        <div className="ldr-unmatched-notice">
          {unmatched.length} topic{unmatched.length !== 1 ? 's' : ''} from Law Drills
          could not be matched to a topic area. The data has been saved and matching will improve over time.
          <ul className="ldr-unmatched-list">
            {unmatched.map(t => (
              <li key={t.id}>{t.source_label}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="ldr-l3-notice">
        Results are applied at topic area level. All subtopics within each matched area have been updated as estimated MCQ accuracy.{' '}
        <span title="Your MCQ result for this topic area has been applied to its subtopics as an estimate." style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '2px' }}>What does this mean?</span>
      </div>

      <div className="ldr-cta-row">
        {unmatched.length > 0 ? (
          <>
            <button
              className="ldr-cta-btn"
              onClick={() => {
                const el = document.querySelector('.ldr-unmatched-notice');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              Review unmatched topics
            </button>
            <a className="ldr-cta-btn ldr-cta-btn-outline" href="/">View dashboard</a>
          </>
        ) : (
          <a className="ldr-cta-btn" href="/">View dashboard</a>
        )}
      </div>

      {/* ── Review / override section ── */}
      <div className="ldr-review-section">
        <div className="ldr-review-heading">Review topic matches</div>
        <div className="ldr-review-hint">
          If a topic area was matched incorrectly, reassign it using the dropdown.
          Your corrections are saved for future imports.
        </div>

        {feedback && (
          <div className={`ldr-feedback ${feedback.type}`}>{feedback.msg}</div>
        )}

        <table className="ldr-table">
          <thead>
            <tr>
              <th>Law Drills topics</th>
              <th>Score</th>
              <th>SQETrack topic area</th>
            </tr>
          </thead>
          <tbody>
            {topics.map(t => {
              const currentGT = overrides.hasOwnProperty(t.id)
                ? overrides[t.id]
                : t.sqetrack_general_topic;
              const isEdited = overrides.hasOwnProperty(t.id);

              return (
                <tr key={t.id}>
                  <td className="ldr-source">
                    {t.l3_source_labels && t.l3_source_labels.length > 0
                      ? t.l3_source_labels.join(' · ')
                      : (t.source_label || '—')
                    }
                    {t.attempted != null && (
                      <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 12 }}>
                        ({t.attempted}q)
                      </span>
                    )}
                  </td>
                  <td className="ldr-score">
                    {t.score_pct != null ? `${Math.round(t.score_pct)}%` : '—'}
                  </td>
                  <td className="ldr-match-cell">
                    <select
                      className="ldr-select"
                      defaultValue={currentGT || ''}
                      onChange={e => setOverrides(prev => ({
                        ...prev,
                        [t.id]: e.target.value || null,
                      }))}
                    >
                      <option value="">— Not matched —</option>
                      {allL2s.map((l2, i) => (
                        <option key={i} value={l2.general_topic}>{l2.label}</option>
                      ))}
                    </select>
                    {isEdited && (
                      <span className="conf-badge" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                        edited
                      </span>
                    )}
                    {!isEdited && t.sqetrack_general_topic && (
                      <span className={`conf-badge conf-${t.confidence || 'none'}`}>
                        {t.confidence || 'none'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="ldr-actions">
          <button
            className="ldr-save-btn"
            disabled={saving || !pendingCount}
            onClick={handleSave}
          >
            {saving ? 'Saving' : `Save corrections${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
          </button>
          {pendingCount > 0 && (
            <button
              className="ldr-discard-btn"
              onClick={() => setOverrides({})}
            >
              Discard changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
